import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { hardwareAPI, type LicenceType } from '../../services';
import { ALL_MONITORING_FUNCTIONS } from '../pour/PourFunctionSelector';

interface EditorState {
  id: string | null; // null = creating
  name: string;
  functions: string[];
  sortOrder: string;
  isActive: boolean;
}

const EMPTY_EDITOR: EditorState = {
  id: null,
  name: '',
  functions: [],
  sortOrder: '',
  isActive: true,
};

interface LicenceTypeManagerDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Super-admin management of hardware licence tiers — billing constructs
 *  assigned per logger; they do not gate pour functions. */
const LicenceTypeManagerDialog: React.FC<LicenceTypeManagerDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState<LicenceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [editor, setEditor] = useState<EditorState | null>(null);

  const fetchTiers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setTiers(await hardwareAPI.getLicenceTypes());
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load licence types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setEditor(null);
      fetchTiers();
    }
  }, [open, fetchTiers]);

  const startCreate = () => setEditor({ ...EMPTY_EDITOR });

  const startEdit = (tier: LicenceType) =>
    setEditor({
      id: tier.LicenceTypeID,
      name: tier.Name,
      functions: (tier.CoveredFunctions || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      sortOrder: tier.SortOrder != null ? String(tier.SortOrder) : '',
      isActive: tier.IsActive !== false,
    });

  const toggleFunction = (name: string) => {
    if (!editor) return;
    setEditor({
      ...editor,
      functions: editor.functions.includes(name)
        ? editor.functions.filter((f) => f !== name)
        : [...editor.functions, name],
    });
  };

  const handleSave = async () => {
    if (!editor || !editor.name.trim()) return;
    setSaving(true);
    setError('');
    const payload = {
      Name: editor.name.trim(),
      CoveredFunctions: editor.functions.join(', '),
      SortOrder: editor.sortOrder ? Number(editor.sortOrder) : undefined,
      IsActive: editor.isActive,
    };
    try {
      if (editor.id) {
        await hardwareAPI.updateLicenceType(editor.id, payload);
      } else {
        await hardwareAPI.createLicenceType(payload);
      }
      setEditor(null);
      await fetchTiers();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to save licence type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tier: LicenceType) => {
    setSaving(true);
    setError('');
    try {
      const result = await hardwareAPI.deleteLicenceType(tier.LicenceTypeID);
      if (result.deactivated) {
        setError(
          t('hardware.licenceDeactivatedNote', {
            defaultValue:
              '"{{name}}" is assigned to {{count}} logger(s), so it was deactivated instead of deleted.',
            name: tier.Name,
            count: result.logger_count,
          }),
        );
      }
      await fetchTiers();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete licence type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('hardware.manageLicenceTypes', 'Manage Licence Types')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'hardware.licenceTypesHint',
            'Licence tiers record what each device is billed for. They are assigned per logger on the hardware form and do not restrict pour functions.',
          )}
        </Typography>
        {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name', 'Name')}</TableCell>
                <TableCell>{t('hardware.coveredFunctions', 'Covered Functions')}</TableCell>
                <TableCell align="center">{t('hardware.loggers', 'Loggers')}</TableCell>
                <TableCell align="center">{t('common.active', 'Active')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {tiers.map((tier) => (
                <TableRow key={tier.LicenceTypeID} sx={{ opacity: tier.IsActive === false ? 0.55 : 1 }}>
                  <TableCell sx={{ fontWeight: 500 }}>{tier.Name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(tier.CoveredFunctions || '')
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .map((f) => (
                          <Chip key={f} label={f} size="small" variant="outlined" />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">{tier.LoggerCount ?? 0}</TableCell>
                  <TableCell align="center">
                    {tier.IsActive === false
                      ? t('common.no', 'No')
                      : t('common.yes', 'Yes')}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => startEdit(tier)} aria-label={t('common.edit', 'Edit')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <Tooltip
                      title={
                        (tier.LoggerCount ?? 0) > 0
                          ? t('hardware.licenceInUseTooltip', 'Assigned to loggers, so deleting will deactivate it instead')
                          : ''
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(tier)}
                        disabled={saving}
                        aria-label={t('common.delete', 'Delete')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {tiers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    {t('hardware.noLicenceTypes', 'No licence types configured yet')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {editor && (
          <Box sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {editor.id
                ? t('hardware.editLicenceType', 'Edit Licence Type')
                : t('hardware.addLicenceType', 'Add Licence Type')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
              <TextField
                size="small"
                label={t('common.name', 'Name')}
                value={editor.name}
                onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                sx={{ minWidth: 260 }}
                required
              />
              <TextField
                size="small"
                type="number"
                label={t('hardware.sortOrder', 'Sort Order')}
                value={editor.sortOrder}
                onChange={(e) => setEditor({ ...editor, sortOrder: e.target.value })}
                sx={{ width: 120 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editor.isActive}
                    onChange={(e) => setEditor({ ...editor, isActive: e.target.checked })}
                  />
                }
                label={t('common.active', 'Active')}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t('hardware.coveredFunctions', 'Covered Functions')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {ALL_MONITORING_FUNCTIONS.map((name) => (
                <FormControlLabel
                  key={name}
                  control={
                    <Checkbox
                      size="small"
                      checked={editor.functions.includes(name)}
                      onChange={() => toggleFunction(name)}
                    />
                  }
                  label={<Typography variant="body2">{name}</Typography>}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={saving || !editor.name.trim()}
              >
                {editor.id ? t('common.save', 'Save') : t('common.create', 'Create')}
              </Button>
              <Button size="small" onClick={() => setEditor(null)} disabled={saving}>
                {t('common.cancel', 'Cancel')}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!editor && (
          <Button startIcon={<AddIcon />} onClick={startCreate}>
            {t('hardware.addLicenceType', 'Add Licence Type')}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LicenceTypeManagerDialog;
